import {ref} from 'vue';
import popupUtil from '@/commons/popupUtil';
import RefType from '@/commons/wildcards/constants/RefType.js';
import {ModuleBudgetKindItem} from '@/stores/moduleConst';
import {ModuleBudgetItem} from '@/stores/moduleConst';
import {ModuleDefaultAccountCache} from '@/stores/moduleConst';
import commonFunction from '@/commons/commonFunction.js';
import _ from 'lodash';

export const useActionGridEditor = proxy => {
  const refTypeCalculateUnitPrice = [
    RefType.BUPlanWithdrawCash,
    RefType.BUPlanWithdrawDeposit,
    RefType.BUPlanWithdrawTransfer,
    RefType.BUPlanWithdrawSalaryTransfer,
    RefType.BUTransfer,
    RefType.BUTransferDeposit,
    RefType.BUTreasuryFundTransferSalary,
    RefType.BUTreasuryFundTransferInsurancePayment,
    RefType.BUTransferFixedAsset,
    RefType.CAReceipt,
    RefType.CAReceiptPlanWithdraw,
    RefType.CAPayment,
    RefType.CAPaymentFixedAsset,
    RefType.CAPaymentVendor,
    RefType.CAPaymentSalary,
    RefType.CAPaymentDeposit,
    RefType.CAReceiptWithdraw,
    RefType.BAWithdrawPurchase,
    RefType.BABankTransfer,
    RefType.GLGeneralVoucher,
    RefType.BAWithDrawFixedAsset,
    RefType.GLBeginningYearTransfer,
    RefType.GLProfitLossTransfer,
  ];

  /**
   * Gán lại hạch toán đồng thời các dòng trống trường hợp sinh hạch toán ko có dữ liệu (áp dụng khi sinh chứng từ)
   * NXCHIEN 10.11.2022
   */
  function assignDataEmptyToParallel(me) {
    const defaultRow = me._defaultRow != null && me._defaultRow !== undefined ? me._defaultRow : 3;
    //NVTUAN1 01.03.2024: Sử dụng hàm hasOwnProperty không ảnh hưởng bởi PBI 419812
    if (me.model && me.model.hasOwnProperty('Parallels')) {
      // Tìm xem trong mảng dữ liệu hạch toán đồng thời có dữ liệu hay không
      var arr = [];
      if (me.model.Parallels && Array.isArray(me.model.Parallels)) {
        // NXCHIEN sửa lỗi sinh chứng từ chưa xóa dòng hạch toán đồng thời cũ
        const deleteItems = me.model.Parallels.filter(x => x.state == me.$ms.constant.ModelState.Delete) || [];
        arr = arr.concat(deleteItems);

        const insertArrayRecord = me.model.Parallels.filter(item =>
          [me.$ms.constant.ModelState.Insert, me.$ms.constant.ModelState.Update].includes(item.state),
        );
        if (insertArrayRecord && insertArrayRecord.length > 0) {
          return;
        }
      }

      for (let index = 0; index < defaultRow; index++) {
        const emptyRow = {state: me.$ms.constant.ModelState.Empty};
        arr.push(emptyRow);
      }

      me.model.Parallels = arr;
    }
  }

  /**
   * Cập nhật dữ liệu 1 dòng khi sửa dòng dữ liệu trên grid
   * NOTE: Nghiệp vụ tương tư hàm copyDownRow: nếu sửa hàm này thì cần check lại hàm copyDownRow bên dưới
   * @param {*} $grid
   * @param {*} col
   * @param {*} rowIndex
   * @param {*} metaData
   * @param {*} callbackUpdateRow
   * NXCHIEN 14.06.2022
   */
  const changeDataBusinessCommon = ($grid, col, rowIndex, metaData, callbackUpdateRow, dataField, tracking) => {
    let budgetItems = [];
    let budgetKindItems = [];
    switch (dataField) {
      case 'budget_kind_item_code':
        let sub_kind_code = $grid.dataView[rowIndex].budget_sub_kind_item_code;
        if (sub_kind_code) {
          budgetKindItems = proxy.$store.getters[`${ModuleBudgetKindItem}/items`];
          let sub_kind_misa_code = budgetKindItems.find(x => x.budget_kind_item_code == sub_kind_code)?.misa_code_id;
          let budgetKind = budgetKindItems.find(x => x.grade == 1 && sub_kind_misa_code.startsWith(x.misa_code_id));
          if (!budgetKind || budgetKind.budget_kind_item_code != tracking.newVal) {
            callbackUpdateRow({budget_sub_kind_item_code: null}, rowIndex);
          }
        }
        break;
      case 'budget_sub_kind_item_code':
        budgetKindItems = proxy.$store.getters[`${ModuleBudgetKindItem}/items`];
        let sub_kind_misa_code = budgetKindItems.find(x => x.budget_kind_item_code == tracking.newVal)?.misa_code_id;
        let budgetKind = budgetKindItems.find(
          x => !x.inactive && x.grade == 1 && sub_kind_misa_code?.startsWith(x.misa_code_id),
        );
        callbackUpdateRow(
          {
            budget_kind_item_code: budgetKind ? budgetKind.budget_kind_item_code : null,
          },
          rowIndex,
        );
        break;
      case 'budget_item_code':
        let sub_item_code = $grid.dataView[rowIndex].budget_sub_item_code;
        if (sub_item_code) {
          budgetItems = proxy.$store.getters[`${ModuleBudgetItem}/items`];
          let budgetSubItem = budgetItems.find(x => x.budget_item_code == sub_item_code);
          let budgetItem = budgetItems.find(x => x.budget_item_id == budgetSubItem?.parent_id);
          if (!budgetItem || budgetItem.budget_item_code != tracking.newVal) {
            callbackUpdateRow({budget_sub_item_code: null}, rowIndex);
          }
        }

        break;
      case 'budget_detail_item_code':
        budgetItems = proxy.$store.getters[`${ModuleBudgetItem}/items`];
        let budgetDetailItem = budgetItems.find(x => x.budget_item_code == tracking.newVal);
        let resultDetail = budgetItems.find(x => x.budget_item_id == budgetDetailItem?.parent_id);
        if (resultDetail) {
          callbackUpdateRow(
            {
              budget_item_code: resultDetail?.parent_code,
              budget_sub_item_code: resultDetail?.budget_item_code,
            },
            rowIndex,
          );
        }
        break;
      case 'budget_sub_item_code':
        budgetItems = proxy.$store.getters[`${ModuleBudgetItem}/items`];
        let budgetSubItem = budgetItems.find(x => x.budget_item_code == tracking.newVal);
        let result = budgetItems.find(x => x.budget_item_id == budgetSubItem?.parent_id);
        if (result) {
          // kiểm tra dboption nếu có lấy dữ liệu theo mục tiểu mục
          if (proxy.dbOptions.UseBugetSubItemNameForDescription) {
            // Kiểm tra các chứng từ liên quan đến VTHH sẽ không bind diễn giải của danh mục
            let refTypeVTHH = [
              RefType.CAPaymentPurchase,
              RefType.BAWithdrawPurchase,
              RefType.PUInvoice,
              RefType.BUTransferPurchase,
              RefType.INInward,
              RefType.INTransfer,
              RefType.INAdjustment,
              RefType.INInwardFromINAdjustment,
              RefType.INInwardFromINAdjustmentChangeAmount,
              RefType.INOutwardFromINAdjustment,
              RefType.INOutwardFromINAdjustmentChangeAmount,
              RefType.INOutward,
              RefType.BADepositSale,
              RefType.SAInvoice,
              RefType.SAReturn,
              RefType.CAReceiptSale,
              RefType.FAIncrement,
              RefType.FADecrement,
              RefType.FAAdjustment,
              RefType.FADepreciation,
              RefType.FADevaluation,
              RefType.FAPurchaseInvoice,
              RefType.FATransfer,
              RefType.CAPaymentFixedAsset,
              RefType.BAWithDrawFixedAsset,
              RefType.BUTransferFixedAsset,
            ];
            if (!(proxy && proxy.model && refTypeVTHH.includes(proxy.model.ref_type))) {
              $grid.dataView[rowIndex].description = budgetSubItem.budget_item_name;
            }
          }

          callbackUpdateRow({budget_item_code: result.budget_item_code}, rowIndex);
        }
        break;
      case 'amount_oc':
        let amountData = commonFunction.roundDecimalDbOption(
          proxy.$ms.constant.DbOptionKey.CurrencyDecimalDigits,
          tracking.newVal * (proxy.model.exchange_rate || 1),
        );
        callbackUpdateRow({amount: amountData}, rowIndex);
        if (refTypeCalculateUnitPrice.includes(proxy.model.ref_type || proxy.refType)) {
          const quantity = $grid.dataView[rowIndex].quantity;
          if (quantity && amountData) {
            let unitPrice = commonFunction.roundDecimalDbOption(
              proxy.$ms.constant.DbOptionKey.CurrencyUnitPriceDigits,
              amountData / quantity,
            );
            callbackUpdateRow({unitprice: unitPrice}, rowIndex);
          }
        }
        break;
      case 'amount':
        if (refTypeCalculateUnitPrice.includes(proxy.model.ref_type || proxy.refType)) {
          const quantity = $grid.dataView[rowIndex].quantity;
          if (quantity && tracking.newVal) {
            let unitPrice = commonFunction.roundDecimalDbOption(
              proxy.$ms.constant.DbOptionKey.CurrencyUnitPriceDigits,
              tracking.newVal / quantity,
            );
            callbackUpdateRow({unitprice: unitPrice}, rowIndex);
          }
        }

        // NXCHIEN 23.10.2023 nếu là hao mòn thì tính lại tổng tiền. Cải tiến hiệu năng do dữ liệu lớn ko computed nữa
        if (proxy.model.ref_type == proxy.$ms.constant.RefType.FADepreciation) {
          let newAmount = (tracking.newVal ?? 0) - (tracking.oldVal ?? 0);
          proxy.totalAmountComputed += newAmount;
        }
        break;
      case 'budget_source_name':
        if (metaData.default_method_distribute_type !== null && metaData.default_method_distribute_type !== undefined) {
          $grid.dataView[rowIndex].method_distribute_type = metaData.default_method_distribute_type;
          $grid.dataView[rowIndex].method_distribute_name = proxy.$ms.format.formatData(
            metaData.default_method_distribute_type,
            {
              formatType: 100,
              enumName: 'MethodDistributeType',
            },
          );
        }

        $grid.dataView[rowIndex].budget_source_group_property_type = metaData.group_property_type;
        break;
      case 'accounting_object_code':
        //NNLINH 10.04.2023 thi công lấy thêm tên đối tượng detail
        callbackUpdateRow({accounting_object_name: metaData.accounting_object_name}, rowIndex);
        break;
      case 'project_code':
        callbackUpdateRow({project_expense_id: null, project_expense_code: null}, rowIndex);
        break;
      case 'quantity':
        if (tracking.newVal > 0) {
          let newUnitPrice =
            (($grid.dataView[rowIndex].amount_oc || $grid.dataView[rowIndex].amount || 0) *
              (proxy.model.exchange_rate || 1)) /
            tracking.newVal;
          callbackUpdateRow(
            {
              unitprice: commonFunction.roundDecimalDbOption(
                proxy.$ms.constant.DbOptionKey.CurrencyUnitPriceDigits,
                newUnitPrice,
              ),
            },
            rowIndex,
          );
        }
        break;
      case 'debit_account':
        //nếu nhập TK Nợ 511x, 512x thì cột Nghiệp vụ mặc định là Nộp phục hồi - Thực chi
        //Lưu ý không bao gồm Xác định kết quả hoạt động, Quyết toán số dư đầu năm, Kết chuyển số dư cuối năm (Đã kiểm tra -các màn này không cập nhật NV theo debit_account - 26/12/2023)
        if (metaData.account_number?.startsWith('511') || metaData.account_number?.startsWith('512')) {
          let cashWithdrawTypeId = proxy.$ms.constant.CashWithDrawType.RestoreExpense;
          let cashWithdrawTypeName = proxy.$ms.format.formatData(cashWithdrawTypeId, {
            formatType: 100,
            enumName: 'CashWithDrawType',
          });
          callbackUpdateRow(
            {
              cash_withdraw_type_id: cashWithdrawTypeId,
              cash_withdraw_type_name: cashWithdrawTypeName,
            },
            rowIndex,
          );
        }
        break;
      default:
        break;
    }
  };

  const defaultData = ref({});
  /**
   * Style grid đổi màu chữ theo hệ thống tùy chọn
   * @param {*} col
   * @param {*} dataRow
   * @param {*} styleObject
   * NXCHIEN 06.07.2022
   */
  function onGridStyleColumn(col, dataRow, styleObject, me) {
    let obj = {};
    if (dataRow) {
      if (!dataRow.is_posted) {
        obj = {
          color: me.dbOptions.UnPostedVoucherForeColor,
        };
        Object.assign(styleObject, obj);
      }

      if (col.columnType == 'ColumnNumber' && dataRow[col.dataField] < 0) {
        obj = {
          color: me.dbOptions.CurrencyNegativeForeColor,
        };
        Object.assign(styleObject, obj);
      }
    }
  }

  /**
   * Lấy dữ liệu mặc định
   * NXCHIEN 06.07.2022
   */
  const getDataDefault = value => {
    const accountDefaultCache = proxy.$store.getters[`${ModuleDefaultAccountCache}/items`];
    defaultData.value = accountDefaultCache.find(x => x.ref_type == value);
  };

  /**
   * Sự kiện click vào dòng dữ liệu detail
   * @param {*} colIndex
   * @param {*} rowIndex
   * NXCHIEN 06.07.2022
   * NVTUAN1 31.01.2023: Thêm tham số isNotCloneRow: Không clone row trên xuống row dưới (true: không clone, false: có clone) để custom đặc thù ở các màn hình yêu cầu bỏ chức năng clone dòng
   * NVTUAN1 08.02.2023: Sửa lại logic code và comment
   */
  const toggleAboveHandlerGrid = (colIndex, rowIndex, $grid, me, isNotCloneRow) => {
    if (me.toggleAbove && ((me._defaultRow && rowIndex >= me._defaultRow) || (!me._defaultRow && rowIndex >= 3))) {
      me.toggleAbove = false;
    }

    let prevRow = $grid.getRowByViewIndex(rowIndex - 1);
    let currentRow = $grid.getRowByViewIndex(rowIndex);

    //Nếu cho phép clone dòng thì mới thực hiện
    if (me.dbOptions && me.dbOptions.AllowCloneDetailVoucherRow) {
      // Nếu dòng đầu tiên hoạc Dòng liền trước là dòng emtpy thì luôn lấy dữ liệu mặc định
      if (prevRow == null || prevRow?.state === me.$ms.constant.ModelState.Empty) {
        bindDefaultData($grid, me, rowIndex);
      }
      //Dòng empty và dòng liền trước là dòng dữ liệu thì mới clone row
      else if (
        prevRow != null &&
        currentRow?.state === me.$ms.constant.ModelState.Empty &&
        prevRow?.state !== me.$ms.constant.ModelState.Empty
      ) {
        //Nếu truyền tham số isNotCloneRow = true (không cho phép clone dòng dữ liệu trên grid) thì chỉ binding dữ liệu mặc định
        if (isNotCloneRow) {
          bindDefaultData($grid, me, rowIndex);
        } else {
          currentRow = cloneRow(rowIndex - 1, currentRow, $grid, me);
        }
      }
    } else {
      bindDefaultData($grid, me, rowIndex);
    }
  };

  /**
   * Clone row liền trước
   * NXCHIEN 11.07.2020 : Lưu ý hàm clone trong base dính refrence.
   * NCPHUOC 21/07/2022: Thêm field selected mặc định sẽ bỏ qua của grid editor
   */
  function cloneRow(rowIndex, item, $grid, me) {
    let preRow = _.cloneDeep($grid.getRowByViewIndex(rowIndex));
    let clearFields = [];
    if (proxy.clearFields) {
      clearFields = proxy.clearFields;
    } else {
      clearFields = ['state', 'ref_id', 'ref_detail_id', 'selected', 'amount', 'amount_oc', 'indexRow'].concat(
        $grid.columnx.filter(x => x.columnType === me.$ms.constant.ColumnType.Number).map(x => x.dataField),
      );
    }
    if (!clearFields.includes('selected')) {
      clearFields.push('selected');
    }
    clearFields.forEach(field => delete preRow[field]);
    Object.assign(item, {...preRow});
    item.state = me.$ms.constant.ModelState.Empty;

    // Custom lại dữ liệu sao chép dòng
    if (me.customCloneRow && typeof me.customCloneRow == 'function') {
      me.customCloneRow(item);
    }

    return item;
  }

  /**
   * Bind dữ liệu mặc định vào dòng chi tiết
   * @param {*} $grid
   * @param {*} me
   * @param {*} rowIndex
   * @param {*} isAlwayBind: luôn bind (Ghi tăng TSCĐ)
   * NXCHIEN 11.07.2022
   */
  function bindDefaultData($grid, me, rowIndex, isAlwayBind) {
    let currentRow = $grid.getRowByViewIndex(rowIndex);
    // Xử lý lấy mặc định cho các dòng dữ liệu
    if (currentRow && (currentRow.state == me.$ms.constant.ModelState.Empty || isAlwayBind)) {
      // Xử lý dữ liệu mặc định TK nợ có.
      if (defaultData.value && $grid.name != 'gridParallel') {
        if ($grid.columnFull && $grid.columnFull.find(x => x.dataField == 'credit_account')) {
          currentRow.credit_account = defaultData.value.allow_default_setting
            ? defaultData.value.default_credit_account_id
            : null;
        }
        if ($grid.columnFull && $grid.columnFull.find(x => x.dataField == 'debit_account')) {
          currentRow.debit_account = defaultData.value.allow_default_setting
            ? defaultData.value.default_debit_account_id
            : null;
        }
        if ($grid.columnFull && $grid.columnFull.find(x => x.dataField == 'tax_account')) {
          currentRow.tax_account = defaultData.value.allow_default_setting
            ? defaultData.value.default_tax_account_id
            : null;
        }
      }

      //#region Bind dữ liệu mặc định
      //Lấy DB Option thêm MỤC lục NGÂN SÁCH
      // Nguồn
      if (
        me.dbOptions.DefaultBudgetSourceID &&
        me.dbOptions.DefaultBudgetSourceID != me.$ms.constant.FixValue.GuidEmpty
      ) {
        currentRow.budget_source_id = me.dbOptions.DefaultBudgetSourceID;
        currentRow.budget_source_name = me.dbOptions.DefaultBudgetSourceName;
        // lấy thêm DefaultBudgetSourceGroupPropertyType
        currentRow.budget_source_group_property_type = me.dbOptions.DefaultBudgetSourceGroupPropertyType;
      }
      // Chương
      if (me.dbOptions.DefaultBudgetChapterCode) {
        currentRow.budget_chapter_code = me.dbOptions.DefaultBudgetChapterCode;
        currentRow.budget_chapter_id = me.dbOptions.DefaultBudgetChapterId;
      }

      // Loại
      if (me.dbOptions.DefaultBudgetKindItemCode) {
        currentRow.budget_kind_item_code = me.dbOptions.DefaultBudgetKindItemCode;
        currentRow.budget_kind_item_id = me.dbOptions.DefaultBudgetKindItemId;
      }
      // Khoản
      if (me.dbOptions.DefaultBudgetSubKindItemCode) {
        currentRow.budget_sub_kind_item_code = me.dbOptions.DefaultBudgetSubKindItemCode;
        currentRow.budget_sub_kind_item_id = me.dbOptions.DefaultBudgetSubKindItemId;
      }
      // nghiệp vụ
      if (me.dbOptions.DefaultCashWithDrawTypeID != null && me.dbOptions.DefaultCashWithDrawTypeID != undefined) {
        currentRow.cash_withdraw_type_id = me.dbOptions.DefaultCashWithDrawTypeID;
        currentRow.cash_withdraw_type_name = me.$ms.format.formatData(me.dbOptions.DefaultCashWithDrawTypeID, {
          formatType: 100,
          enumName: 'CashWithDrawType',
        });
      }

      // Hình thức cấp phát
      if (me.dbOptions.DefaultMethodDistributeID != null && me.dbOptions.DefaultMethodDistributeID != undefined) {
        currentRow.method_distribute_type = me.dbOptions.DefaultMethodDistributeID;
        currentRow.method_distribute_name = me.$ms.format.formatData(me.dbOptions.DefaultMethodDistributeID, {
          formatType: 100,
          enumName: 'MethodDistributeType',
        });
      }

      // Loại quỹ
      if (me.dbOptions.DefaultFundID) {
        let refTypeNotUpdateFund = [RefType.GLGeneralVoucher];
        if (!refTypeNotUpdateFund.includes(proxy.model.ref_type) || proxy.model.IsInvoiceGenerateData) {
          currentRow.fund_id = me.dbOptions.DefaultFundID;
          currentRow.fund_code = me.dbOptions.DefaultFundCode;
        }
      }

      // Nhóm hàng hóa dịch vụ
      if (
        me.dbOptions.DefaultPurchasePurposeID &&
        $grid.columnFull &&
        $grid.columnFull.find(x => x.dataField == 'purchase_purpose_code')
      ) {
        currentRow.purchase_purpose_id = me.dbOptions.DefaultPurchasePurposeID;
        currentRow.purchase_purpose_code = me.dbOptions.DefaultPurchasePurposeCode;
      }

      // diễn giải theo diễn giải chung
      if (me.dbOptions.GetMasterDescription) {
        // Kiểm tra các chứng từ liên quan đến VTHH, TSCĐ, CCDC sẽ không bind diễn giải theo diễn giải chung
        let refTypes = [
          // VTHH
          RefType.CAPaymentPurchase,
          RefType.BAWithdrawPurchase,
          RefType.BUTransferPurchase,
          RefType.PUInvoice,
          RefType.INInward,
          RefType.INInwardFromINAdjustment,
          RefType.INInwardFromINAdjustmentChangeAmount,
          RefType.INOutward,
          RefType.INOutwardFromINAdjustment,
          RefType.INOutwardFromINAdjustmentChangeAmount,
          RefType.INTransfer,
          RefType.INAdjustment,
          RefType.BADepositSale,
          RefType.CAReceiptSale,
          RefType.SAInvoice,
          RefType.SAReturn,

          // TSCĐ
          RefType.BUTransferFixedAsset,
          RefType.CAPaymentFixedAsset,
          RefType.BAWithDrawFixedAsset,
          RefType.FAPurchaseInvoice,
          RefType.FAIncrement,
          RefType.FATransfer,
          RefType.CAReceiptFixedAsset,
          RefType.BADepositFixedAsset,
          RefType.FASaleInvoice,
          // CCDC
          RefType.SUTransfer,
          RefType.SUIncrement,
          RefType.SUDecrement,
        ];
        if (proxy && proxy.model && !refTypes.includes(proxy.model.ref_type)) {
          currentRow.description = me.model.journal_memo;
        }
      }

      // Nếu master có CTMT thì sẽ lấy của master xuống
      if (me.model.project_code) {
        currentRow.project_code = me.model.project_code;
        currentRow.project_id = me.model.project_id;
      } else {
        currentRow.project_code = null;
        currentRow.project_id = null;
      }

      // Nếu master có Đối tượng nhận thì sẽ lấy của master xuống
      if (me.model.accounting_object_code) {
        currentRow.accounting_object_code = me.model.accounting_object_code;
        currentRow.accounting_object_name = me.model.accounting_object_name;
        currentRow.accounting_object_id = me.model.accounting_object_id;
      } else {
        currentRow.accounting_object_code = null;
        currentRow.accounting_object_name = null;
        currentRow.accounting_object_id = null;
      }
      //#endregion

      //Gán thêm property sort_order mặc định là null
      //TVDAT 27/07/2022
      if ($grid.dataView && Array.isArray($grid.dataView) && $grid.dataView.length > 0) {
        $grid.dataView.forEach(element => {
          element.sort_order = null;
        });
      }
      if (me.customDefaultData && typeof me.customDefaultData == 'function') {
        me.customDefaultData($grid, me, rowIndex);
      }
    }
  }

  /**
   * Custom default data grid
   * @param {*} $grid
   * @param {*} me
   * @param {*} rowIndex
   * NNLINH 18.07.2022
   */
  function customDefaultData($grid, me, rowIndex) {}

  /**
   * click icon ckc mở form chọn cam kết chi
   * NXCHIEN 07.07.2022
   */
  function onClickCKC(me) {
    popupUtil.show('BUOpeningCommitmentPopup', {
      mode: me.$ms.constant.FormState.Add,
      data: me.model,
      options: {
        submit: data => {
          // Lấy key grid hạch toán
          let keyModel = '';
          for (const key in me._gridDetails) {
            const element = me._gridDetails[key];
            if (element === 'refDetail') {
              keyModel = key;
            }
          }

          me.model.bu_commitment_request_ref_no = data?.ckcRefNo;
          me.model.bu_commitment_request_id = data?.ckcID == undefined || data?.ckcID == '' ? null : data?.ckcID;

          if (data && data.gridDetail && data.gridDetail.length) {
            // lấy ra index lớn nhất
            let indexMax = me.model[keyModel].findLastIndex(x => x.state != me.$ms.constant.ModelState.Empty) || 0;

            let restIndex = me.model[keyModel].length - 1 - indexMax;

            // thêm data.gridDetail.length - restIndex dòng nữa
            let length = data.gridDetail.length - restIndex;
            for (let i = 0; i < length; i++) {
              me.$refs['refDetail'].addRow(me.$refs['refDetail'].datax.length, me.model[keyModel].length + i, true);
            }

            for (let i = 0; i < data.gridDetail.length; i++) {
              let item = data.gridDetail[i];
              item.amount_oc = item.amount;

              Object.keys(item).forEach(x => {
                if (!item[x]) {
                  delete item[x];
                }
              });

              // lấy default cho data.gridDetail.length dòng
              bindDefaultData(me.$refs['refDetail'], me, indexMax + i + 1); // refDetail là key bảng hạch toán
              Object.assign(me.model[keyModel][indexMax + i + 1], {
                ...item,
              });
            }
          }
        },
      },
    });
  }

  /**
   * Thực hiện copy cột theo option tại từng ô editor (copy lên trên, copy xuống dưới) tương tự Ctrl+2
   * @param {*} actionId
   * @param {*} objAssign
   * @param {*} metaData
   * @param {*} column
   * @param {*} $grid
   * @param {*} lastIndex
   * @param {*} rowIndex
   * @param {*} isShowCombo
   * dtnga3 (23.05.2023)
   * Modified DTQUOC 20.10.2023
   */
  function copyActionClick(
    actionId,
    objAssign,
    metaData,
    column,
    $grid,
    lastIndex,
    rowIndex,
    isShowCombo,
    callbackUpdateRow,
  ) {
    switch (actionId) {
      case proxy.$ms.constant.EditorAction.CopyUpRow:
        // lastIndex = rowIndex - 1;
        // rowIndex = 0;
        copyDownRow(objAssign, metaData, column, $grid, lastIndex, rowIndex, isShowCombo, callbackUpdateRow);
        break;
      case proxy.$ms.constant.EditorAction.CopyDownRow:
        if (lastIndex > rowIndex) {
          copyDownRow(objAssign, metaData, column, $grid, lastIndex, rowIndex, isShowCombo, callbackUpdateRow);
        }
        break;
      // trường hợp sao chép dữ liệu
      case proxy.$ms.constant.EditorAction.CopyRow:
        copyDownRow(objAssign, metaData, column, $grid, lastIndex - 1, 0, isShowCombo);
        break;
    }
  }

  /**
   * copy cột xuống các dòng dưới
   * NOTE: Nghiệp vụ tương tư hàm copyDownRow: nếu sửa hàm này thì cần check lại hàm copyDownRow bên dưới
   * @param {*} objAssign : Dữ liệu để merge vào dòng đó
   * @param {*} metaData : Dữ liệu dòng hoặc dữ liệu vừa chọn combo
   * @param {*} column : Cột đang copy
   * @param {*} $grid : Grid
   * @param {*} lastIndex: các dòng được copy rowIndex => lastIndex
   * @param {*} rowIndex: Dòng đang active
   * @param {*} isShowCombo: MetaData lấy từ dòng dữ liệu hay lấy từ xổ combo
   * NOTE: lockCommandAction chỉ lock trong trường hợp case đó có bất đồng bộ
   * NXCHIEN 02.11.2022
   * Modified NHANH3 15.06.2023
   */
  function copyDownRow(objAssign, metaData, column, $grid, lastIndex, rowIndex, isShowCombo, callbackUpdateRow) {
    var idField = $grid.idField;
    for (let i = rowIndex + 1; i < lastIndex + 1; i++) {
      const obj = $grid.dataView[i];
      if (obj.state == proxy.$ms.constant.ModelState.Empty) {
        bindDefaultData($grid, proxy, i);
      }

      // Xét lại validate các cell khi copy dòng
      var row = $grid.$refs[`trEditor_${$grid.dataView[i][idField]}`];
      if (row) {
        row[0].$refs[`cellEditor_${column.dataField}`][0].errorMessage = ``;
      }
    }

    let budgetKindItems = [];
    let budgetItems = [];

    switch (column.dataField) {
      case 'budget_source_name':
        Object.assign(objAssign, {
          budget_source_id: metaData.budget_source_id,
        });
        for (let i = rowIndex; i < lastIndex + 1; i++) {
          let item = $grid.dataView[i];
          let method_distribute_type = isShowCombo
            ? metaData.default_method_distribute_type
            : metaData.method_distribute_type;
          if (method_distribute_type !== null && method_distribute_type !== undefined) {
            item.method_distribute_type = method_distribute_type;
            item.method_distribute_name = proxy.$ms.format.formatData(item.method_distribute_type, {
              formatType: 100,
              enumName: 'MethodDistributeType',
            });
          }

          item.budget_source_group_property_type = isShowCombo
            ? metaData.group_property_type
            : metaData.budget_source_group_property_type;
        }

        break;
      case 'from_budget_source_name':
        Object.assign(objAssign, {
          from_budget_source_id: metaData.budget_source_id || metaData.from_budget_source_id,
        });
        for (let i = rowIndex; i < lastIndex + 1; i++) {
          let item = $grid.dataView[i];
          let method_distribute_type = isShowCombo
            ? metaData.default_method_distribute_type
            : metaData.method_distribute_type;
          if (method_distribute_type !== null && method_distribute_type !== undefined) {
            item.method_distribute_type = method_distribute_type;
            item.method_distribute_name = proxy.$ms.format.formatData(item.method_distribute_type, {
              formatType: 100,
              enumName: 'MethodDistributeType',
            });
          }

          item.budget_source_group_property_type = isShowCombo
            ? metaData.group_property_type
            : metaData.budget_source_group_property_type;
        }

        break;
      case 'to_budget_source_name':
        Object.assign(objAssign, {
          to_budget_source_id: metaData.budget_source_id || metaData.to_budget_source_id,
        });
        for (let i = rowIndex; i < lastIndex + 1; i++) {
          let item = $grid.dataView[i];
          let method_distribute_type = isShowCombo
            ? metaData.default_method_distribute_type
            : metaData.method_distribute_type;
          if (method_distribute_type !== null && method_distribute_type !== undefined) {
            item.method_distribute_type = method_distribute_type;
            item.method_distribute_name = proxy.$ms.format.formatData(item.method_distribute_type, {
              formatType: 100,
              enumName: 'MethodDistributeType',
            });
          }

          item.budget_source_group_property_type = isShowCombo
            ? metaData.group_property_type
            : metaData.budget_source_group_property_type;
        }

        break;
      case 'budget_chapter_code':
        Object.assign(objAssign, {
          budget_chapter_id: metaData.budget_chapter_id,
        });
        break;
      case 'budget_kind_item_code':
        // chỉ gán lại Khoản về null khi Loại khác so với metaData.budget_kind_item_code
        for (let i = rowIndex; i < lastIndex + 1; i++) {
          let item = $grid.dataView[i];
          if (metaData.budget_kind_item_code != item.budget_kind_item_code) {
            item.budget_sub_kind_item_code = null;
            // item.budget_sub_kind_item_id = null;
          }
        }

        break;
      case 'budget_sub_kind_item_code':
        budgetKindItems = proxy.$store.getters[`${ModuleBudgetKindItem}/items`];
        let sub_kind_misa_code = budgetKindItems.find(
          x => x.budget_kind_item_code == objAssign.budget_sub_kind_item_code,
        )?.misa_code_id;
        let budgetKind = budgetKindItems.find(
          x => !x.inactive && x.grade == 1 && sub_kind_misa_code?.startsWith(x.misa_code_id),
        );
        if (budgetKind) {
          for (let i = rowIndex; i < lastIndex + 1; i++) {
            let item = $grid.dataView[i];
            item.budget_kind_item_code = budgetKind.budget_kind_item_code;
          }
        }
        break;
      case 'budget_item_code':
        for (let i = rowIndex; i < lastIndex + 1; i++) {
          let item = $grid.dataView[i];
          if (objAssign.budget_item_code != item.budget_item_code) {
            item.budget_sub_item_code = null;
          }
        }

        break;
      case 'budget_sub_item_code':
        budgetItems = proxy.$store.getters[`${ModuleBudgetItem}/items`];
        let budgetSubItem = budgetItems.find(x => x.budget_item_code == objAssign.budget_sub_item_code);
        let result = budgetItems.find(x => x.budget_item_id == budgetSubItem?.parent_id);

        if (result) {
          // kiểm tra dboption nếu có lấy dữ liệu theo mục tiểu mục
          // Kiểm tra các chứng từ liên quan đến VTHH sẽ không bind diễn giải của danh mục
          let refTypeVTHH = [
            RefType.CAPaymentPurchase,
            RefType.BAWithdrawPurchase,
            RefType.PUInvoice,
            RefType.BUTransferPurchase,
            RefType.INInward,
            RefType.INTransfer,
            RefType.INAdjustment,
            RefType.INInwardFromINAdjustment,
            RefType.INInwardFromINAdjustmentChangeAmount,
            RefType.INOutwardFromINAdjustment,
            RefType.INOutwardFromINAdjustmentChangeAmount,
            RefType.INOutward,
            RefType.BADepositSale,
            RefType.SAInvoice,
            RefType.SAReturn,
            RefType.CAReceiptSale,
            RefType.FAIncrement,
            RefType.FADecrement,
            RefType.FAAdjustment,
            RefType.FADepreciation,
            RefType.FADevaluation,
            RefType.FAPurchaseInvoice,
            RefType.FATransfer,
            RefType.CAPaymentFixedAsset,
            RefType.BAWithDrawFixedAsset,
            RefType.BUTransferFixedAsset,
          ];

          for (let i = rowIndex; i < lastIndex + 1; i++) {
            let item = $grid.dataView[i];

            item.budget_item_code = result.budget_item_code;
            // item.budget_item_id = result.budget_item_id;
            if (
              proxy &&
              proxy.model &&
              !refTypeVTHH.includes(proxy.model.ref_type) &&
              proxy.dbOptions?.UseBugetSubItemNameForDescription
            ) {
              item.description = budgetSubItem.budget_item_name;
            }
          }
        }
        break;
      case 'from_budget_sub_item_code':
        budgetItems = proxy.$store.getters[`${ModuleBudgetItem}/items`];
        let budgetSubItemFrom = budgetItems.find(x => x.budget_item_code == objAssign.from_budget_sub_item_code);
        let resultFrom = budgetItems.find(x => x.budget_item_id == budgetSubItemFrom?.parent_id);

        if (resultFrom) {
          // kiểm tra dboption nếu có lấy dữ liệu theo mục tiểu mục
          // Kiểm tra các chứng từ liên quan đến VTHH sẽ không bind diễn giải của danh mục
          let refTypeVTHH = [
            RefType.CAPaymentPurchase,
            RefType.BAWithdrawPurchase,
            RefType.PUInvoice,
            RefType.BUTransferPurchase,
            RefType.INInward,
            RefType.INTransfer,
            RefType.INAdjustment,
            RefType.INInwardFromINAdjustment,
            RefType.INInwardFromINAdjustmentChangeAmount,
            RefType.INOutwardFromINAdjustment,
            RefType.INOutwardFromINAdjustmentChangeAmount,
            RefType.INOutward,
            RefType.BADepositSale,
            RefType.SAInvoice,
            RefType.SAReturn,
            RefType.CAReceiptSale,
            RefType.FAIncrement,
            RefType.FADecrement,
            RefType.FAAdjustment,
            RefType.FADepreciation,
            RefType.FADevaluation,
            RefType.FAPurchaseInvoice,
            RefType.FATransfer,
            RefType.CAPaymentFixedAsset,
            RefType.BAWithDrawFixedAsset,
            RefType.BUTransferFixedAsset,
          ];

          for (let i = rowIndex; i < lastIndex + 1; i++) {
            let item = $grid.dataView[i];

            item.from_budget_item_code = resultFrom.budget_item_code;
            if (
              proxy &&
              proxy.model &&
              !refTypeVTHH.includes(proxy.model.ref_type) &&
              proxy.dbOptions?.UseBugetSubItemNameForDescription
            ) {
              item.description = budgetSubItemFrom.from_budget_item_name;
            }
          }
        }
        break;
      case 'to_budget_sub_item_code':
        budgetItems = proxy.$store.getters[`${ModuleBudgetItem}/items`];
        let budgetSubItemTo = budgetItems.find(x => x.budget_item_code == objAssign.to_budget_sub_item_code);
        let resultTo = budgetItems.find(x => x.budget_item_id == budgetSubItemTo?.parent_id);

        if (resultTo) {
          // kiểm tra dboption nếu có lấy dữ liệu theo mục tiểu mục
          // Kiểm tra các chứng từ liên quan đến VTHH sẽ không bind diễn giải của danh mục
          let refTypeVTHH = [
            RefType.CAPaymentPurchase,
            RefType.BAWithdrawPurchase,
            RefType.PUInvoice,
            RefType.BUTransferPurchase,
            RefType.INInward,
            RefType.INTransfer,
            RefType.INAdjustment,
            RefType.INInwardFromINAdjustment,
            RefType.INInwardFromINAdjustmentChangeAmount,
            RefType.INOutwardFromINAdjustment,
            RefType.INOutwardFromINAdjustmentChangeAmount,
            RefType.INOutward,
            RefType.BADepositSale,
            RefType.SAInvoice,
            RefType.SAReturn,
            RefType.CAReceiptSale,
            RefType.FAIncrement,
            RefType.FADecrement,
            RefType.FAAdjustment,
            RefType.FADepreciation,
            RefType.FADevaluation,
            RefType.FAPurchaseInvoice,
            RefType.FATransfer,
            RefType.CAPaymentFixedAsset,
            RefType.BAWithDrawFixedAsset,
            RefType.BUTransferFixedAsset,
          ];

          for (let i = rowIndex; i < lastIndex + 1; i++) {
            let item = $grid.dataView[i];

            item.to_budget_item_code = resultTo.budget_item_code;
            if (
              proxy &&
              proxy.model &&
              !refTypeVTHH.includes(proxy.model.ref_type) &&
              proxy.dbOptions?.UseBugetSubItemNameForDescription
            ) {
              item.description = budgetSubItemTo.to_budget_item_name;
            }
          }
        }
        break;
      case 'budget_detail_item_code':
        budgetItems = proxy.$store.getters[`${ModuleBudgetItem}/items`];
        let budgetDetailItem = budgetItems.find(x => x.budget_item_code == objAssign.budget_detail_item_code);
        let resultDetail = budgetItems.find(x => x.budget_item_id == budgetDetailItem?.parent_id);

        if (resultDetail) {
          for (let i = rowIndex; i < lastIndex + 1; i++) {
            let item = $grid.dataView[i];
            item.budget_item_code = result.parent_code;
            item.budget_sub_item_code = result.budget_item_code;
          }
        }
        break;

      case 'activity_name':
        Object.assign(objAssign, {
          activity_id: metaData.activity_id,
        });
        break;
      case 'method_distribute_name':
        Object.assign(objAssign, {
          method_distribute_type: isShowCombo ? metaData.enumValue : metaData.method_distribute_type,
        });
        break;
      case 'cash_withdraw_type_name':
        Object.assign(objAssign, {
          cash_withdraw_type_id: isShowCombo ? metaData.enumValue : metaData.cash_withdraw_type_id,
        });
        break;
      case 'statistic_code':
        Object.assign(objAssign, {
          statistic_id: metaData.statistic_id,
        });
        break;
      case 'accounting_object_code':
        Object.assign(objAssign, {
          accounting_object_id: metaData.accounting_object_id,
          accounting_object_name: metaData.accounting_object_name,
        });
        break;
      case 'topic_code':
        Object.assign(objAssign, {
          topic_id: metaData.topic_id,
        });
        break;
      case 'fund_code':
        Object.assign(objAssign, {
          fund_id: metaData.fund_id,
        });
        break;
      case 'project_code':
        Object.assign(objAssign, {
          project_id: metaData.project_id,
        });
        break;
      case 'from_project_code':
        Object.assign(objAssign, {
          from_project_id: metaData.project_id || metaData.from_project_id,
        });
        break;
      case 'to_project_code':
        Object.assign(objAssign, {
          to_project_id: metaData.project_id || metaData.to_project_id,
        });
        break;
      case 'fund_structure_name':
        Object.assign(objAssign, {
          fund_structure_id: metaData.fund_structure_id,
        });
        break;
      case 'contract_no':
        Object.assign(objAssign, {
          contract_id: metaData.contract_id,
        });
        break;
      case 'task_code':
        Object.assign(objAssign, {
          task_id: metaData.task_id,
        });
        break;
      case 'budget_fees_code':
        Object.assign(objAssign, {
          budget_fees_id: metaData.budget_fees_id,
        });
        break;
      case 'amount_oc':
        let amountData = commonFunction.roundDecimalDbOption(
          proxy.$ms.constant.DbOptionKey.CurrencyDecimalDigits,
          (objAssign.amount_oc || 0) * (proxy.model.exchange_rate || 1),
        );
        Object.assign(objAssign, {amount: amountData});
        break;
      case 'bank_account':
      case 'credit_account':
      case 'budget_provide_code':
      case 'is_source_before_year':
        // các cột này ko cần copy gì thêm (note lại để xem còn thiếu cột nào chưa config ko)
        break;
      case 'debit_account':
        //nếu nhập TK Nợ 511x, 512x thì cột Nghiệp vụ mặc định là Nộp phục hồi - Thực chi
        //Lưu ý không bao gồm Xác định kết quả hoạt động, Quyết toán số dư đầu năm, Kết chuyển số dư cuối năm (Đã kiểm tra -các màn này không cập nhật NV theo debit_account - 26/12/2023)
        if (
          metaData.debit_account?.startsWith('511') ||
          metaData.debit_account?.startsWith('512') ||
          metaData.account_number?.startsWith('511') ||
          metaData.account_number?.startsWith('512')
        ) {
          let cashWithdrawTypeId = proxy.$ms.constant.CashWithDrawType.RestoreExpense;
          let cashWithdrawTypeName = proxy.$ms.format.formatData(cashWithdrawTypeId, {
            formatType: 100,
            enumName: 'CashWithDrawType',
          });
          Object.assign(objAssign, {
            cash_withdraw_type_id: cashWithdrawTypeId,
            cash_withdraw_type_name: cashWithdrawTypeName,
          });
        }
        break;
      case 'debit_accounting_object_code':
        Object.assign(objAssign, {
          debit_accounting_object_id: isShowCombo ? metaData.accounting_object_id : metaData.debit_accounting_object_id,
          debit_accounting_object_name: isShowCombo
            ? metaData.accounting_object_name
            : metaData.debit_accounting_object_name,
        });
        break;
      case 'credit_accounting_object_code':
        Object.assign(objAssign, {
          credit_accounting_object_id: isShowCombo
            ? metaData.accounting_object_id
            : metaData.credit_accounting_object_id,
          credit_accounting_object_name: isShowCombo
            ? metaData.accounting_object_name
            : metaData.credit_accounting_object_name,
        });
        break;
      case 'department_code':
        Object.assign(objAssign, {
          department_id: metaData.department_id,
        });
        break;
      default:
        break;
    }

    if (proxy.copyDownRowExtend && typeof proxy.copyDownRowExtend == 'function') {
      proxy.copyDownRowExtend(objAssign, metaData, column, $grid, lastIndex, rowIndex, isShowCombo, callbackUpdateRow);
    }
  }

  function copyDownRowExtend(objAssign, metaData, column, $grid, lastIndex, rowIndex, isShowCombo, callbackUpdateRow) {
    if (proxy.changeDataBusiness && typeof proxy.changeDataBusiness === 'function') {
      const metaDataTracking = {
        tracking: {
          newVal: objAssign[column.dataField],
        },
      };
      switch (column.dataField) {
        case 'quantity':
        case 'amount':
        case 'amount_oc':
          const direction = lastIndex > rowIndex ? -1 : 1;
          while (lastIndex != rowIndex + direction) {
            proxy.changeDataBusiness($grid, column, lastIndex, metaDataTracking, callbackUpdateRow);
            lastIndex += direction;
          }
          break;
      }
    }
  }

  /**
   * Bổ sung validate ghi sổ, cho grid nhập liệu có phân trang
   * NTLOC 16/4/2024
   */
  function customValidateData(valueControl, compareValue, tilteErr, rowData, col) {
    if (proxy.cacheInvalidPosted?.length > 0) {
      let invalid = proxy.cacheInvalidPosted.filter(x => x.mapField.filter(e => e.mapField == col.dataField)) || [];
      if (
        invalid.length > 0 &&
        !valueControl &&
        invalid.find(
          x =>
            x.mapField.find(e => e.mapField == col.dataField) &&
            (x.key == rowData['debit_account'] || x.key == rowData['credit_account']),
        )
      ) {
        var mes =
          'Bạn cần nhập các thông tin </br>' +
          invalid
            .map(o => {
              return `- <span class="color-green">${o.build.join(
                '; ',
              )}</span> cho các dòng hạch toán tài khoản <span class="color-green">${o.key}</span></br>`;
            })
            .join(' ') +
          `Nếu bạn không thấy <span class="color-green">` +
          commonFunction.distinct(invalid?.reduce((obj, x) => [...obj, ...x.build], [])).join('; ') +
          `</span> trên giao diện chứng từ, vui lòng xem hướng dẫn <a target="_blank" href=${proxy.$tm(
            'i18nHelpLink.ErrorPosted',
          )} class='text-link customCallBack' >tại đây</a>.`;
        return mes;
      }
    }
    return '';
  }

  /**
   * Hàm set thông tin bắt buộc nhập khi ghi sổ, cho grid nhập liệu có phân trang
   * ntloc 16/4/2024
   */
  function setErrorPost(invalid) {
    var me = proxy;
    if (!invalid || invalid.length == 0) {
      return;
    }

    var mapFieldMaster = new Set();
    var mapFieldDetail = [];
    me.resetInfoError();
    // Lấy ra các trường thông tin master bắt buộc nhập khi ghi sổ
    invalid.forEach(element => {
      element.mapField.forEach(field => {
        if (field.isCheckAll == true) {
          mapFieldMaster.add(field.mapField);
          mapFieldDetail.push({field: field.mapField, account: element.key});
        } else {
          mapFieldDetail.push({field: field.mapField, account: element.key});
        }
      });
    });
    mapFieldMaster = Array.from(mapFieldMaster);
    mapFieldDetail = Array.from(mapFieldDetail);
    me.invalidDetailPosted = mapFieldDetail;
    // Lấy key grid hạch toán
    let keyModel = '';
    for (const key in me._gridDetails) {
      const element = me._gridDetails[key];
      if (element === 'refDetail') {
        keyModel = key;
      }
    }
    // lấy ra vị trí index đầu tiên bị lỗi
    let inValidIndex = me.model[keyModel].findIndex(e =>
      mapFieldDetail.find(x => (e.debit_account == x.account || e.credit_account == x.account) && !e[x.field]),
    );
    if (inValidIndex > 0) {
      let index = Math.floor(inValidIndex / me.gridDetail.pageSizeInternal) + 1;
      if (index != me.gridDetail.currentPage) {
        me.gridDetail.$refs.pagination.loadPageIndex(index);
      }
    }

    me.$nextTick(() => {
      setTimeout(() => {
        me.setRequiredPostDetail(mapFieldDetail);
        me.setRequiredPostMaster(mapFieldMaster);
        me.setFocusAfterValidate();
      }, 10);
    });
  }

  /**
   * Lấy danh sách dữ liệu trên grid khác rỗng, dùng cho grid nhập liệu có phân trang
   * ntloc 16/4/2024
   */
  function getDataValidateDictionary(element) {
    const me = proxy;
    return me.$refs[element].data.filter(
      item => item.state != me.$ms.constant.ModelState.Empty && item.state != me.$ms.constant.ModelState.Delete,
    );
  }

  /**
   * Xử lý sau khi validate danh mục, dùng cho grid nhập liệu có phân trang
   * ntloc 16/4/2024
   */
  function processValidateData(itemErrors) {
    const me = proxy;
    // Lấy key grid hạch toán
    let keyModel = '';
    for (const key in me._gridDetails) {
      const element = me._gridDetails[key];
      if (element === 'refDetail') {
        keyModel = key;
      }
    }
    if (!itemErrors || itemErrors.length == 0) {
      return;
    }
    let idField = 'ref_detail_id';

    let inValidIndex = me.model[keyModel].findIndex(x => itemErrors.find(e => x.ref_detail_id == e.item.ref_detail_id));
    let index = Math.floor(inValidIndex / me.gridDetail.pageSizeInternal) + 1;

    if (index != me.gridDetail.currentPage && index > 0) {
      me.gridDetail.$refs.pagination.loadPageIndex(index);
      me.$nextTick(() => {
        setTimeout(() => {
          // Xử lý lỗi
          // Gán thông tin lỗi cho cell
          for (let e of itemErrors) {
            let caption = e.caption;
            let column = e.column;
            let item = e.item;

            if (column.visible) {
              let row = proxy.$refs['refDetail'].$refs[`trEditor_${item[idField]}`];
              if (!row || row.length == 0) {
                continue;
              }

              row[0].$refs[`cellEditor_${column.dataField}`][0].displayError = true;
              row[0].$refs[`cellEditor_${column.dataField}`][0].errorMessage = `${caption} không tồn tại`;

              // Set element lỗi đầu tiên
              if (!proxy.firstElementError) {
                proxy.firstElementError = row[0].$refs[`cellEditor_${column.dataField}`][0];
              }

              // Lấy ra thông tin vị trí của element lỗi đầu tiên trên grid
              if (
                !proxy.infoElementError.colIndex &&
                !proxy.infoElementError.rowIndex &&
                !proxy.infoElementError.col &&
                !proxy.infoElementError.gridName
              ) {
                proxy.infoElementError = {
                  colIndex: row[0].$refs[`cellEditor_${column.dataField}`][0].colIndex,
                  rowIndex: row[0].$refs[`cellEditor_${column.dataField}`][0].rowIndex,
                  col: column,
                  gridName: 'refDetail',
                };
              }
            }
          }
        }, 10);
      });
    }
  }

  /**
   * Lấy dữ liệu của các grid, dùng cho grid nhập liệu có phân trang
   * ntloc 16/4/2024
   */
  function getSummaryDataGrid(...args) {
    const me = proxy;
    let arg = args.filter(x => x !== 'refDetailOther');
    return arg
      .filter(grd => grd)
      .reduce(
        (arr, grd) => [
          ...arr,
          ...(me.$refs[grd]?.data?.filter(
            x => x.state !== me.$ms.constant.ModelState.Delete && x.state !== me.$ms.constant.ModelState.Empty,
          ) || []),
        ],
        [],
      );
  }

  /**
   * Lấy tất cả các tài khoản tab hạch toán, grid nhập liệu có phân trang
   * ntloc 16/4/2024
   */
  function getAllAccount(...args) {
    let me = proxy;
    // lấy key grid hạch toán và hạch toán đồng thời nếu có
    let keyModel = '';
    let keyModelParellel = '';
    for (const key in me._gridDetails) {
      const element = me._gridDetails[key];
      if (element === 'refDetail') {
        keyModel = key;
      } else if (element === 'refDetailParallel') {
        keyModelParellel = key;
      }
    }
    let accountFilter = [
      me.$ms.constant.ColumnCommon.debitAccount,
      me.$ms.constant.ColumnCommon.creditAccount,
      me.$ms.constant.ColumnCommon.taxAccount,
      me.$ms.constant.ColumnCommon.cogsAccount,
      me.$ms.constant.ColumnCommon.inventoryAccount,
    ];
    var listModel = me.model[keyModel];
    if (me.model[keyModelParellel]?.length > 0) {
      listModel = listModel.concat(me.model[keyModelParellel]);
    }
    var accounts = me.$ms.commonFn.distinct(
      listModel.reduce((o, x) => [...o, ...accountFilter.reduce((t, y) => [...t, x[y]], [])], []),
    );
    return accounts.filter(x => x);
  }

  return {
    changeDataBusinessCommon,
    defaultData,
    getDataDefault,
    toggleAboveHandlerGrid,
    onGridStyleColumn,
    bindDefaultData,
    customDefaultData,
    cloneRow,
    onClickCKC,
    copyDownRow,
    copyActionClick,
    assignDataEmptyToParallel,
    copyDownRowExtend,
    customValidateData,
    setErrorPost,
    getDataValidateDictionary,
    processValidateData,
    getSummaryDataGrid,
    getAllAccount,
  };
};
